import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex flex-wrap w-full md:flex-nowrap align-items-center justify-content-between mb-s2" }
const _hoisted_2 = { class: "flex mr-auto align-items-center caption column-gap-s1" }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { class: "flex w-full align-items-center justify-content-between" }
const _hoisted_5 = { class: "flex align-items-center" }

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Token } from '@/sdk/entities/token';
import { addToken } from '@/utils/blockchain';
import { minDecimalsFromCardanoAndMilkomeda } from '@/helpers/milkomeda-wrapped-smartcontract/milkomeda-wsc-calculation';
import { buildSwapWithTokenSymbolsRoute } from '@/helpers/route.helper';
import { Fields } from '@/store/modules/swap/swap.module';
import TokenSelect from '@/components/tokenSelect/TokenSelect.vue';
import AddTokenButton from '@/components/token/AddTokenButton.vue';
import SwapInputBalance from '@/views/pages/swap/components/SwapInputBalance.vue';
import UsdInputValue from '@/views/pages/swap/components/UsdInputValue.vue';
import BaseInputNumber from '@/components/form/BaseInputNumber.vue';
import { SWAP_FORM_INPUTS } from '@/store/modules/swap/constants/SWAP_FORM_INPUTS';
import { DEFAULT_NETWORK_ID, getNetworkId } from '@/helpers/networkParams.helper';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { ENABLE_CROSS_CHAIN } from '@/helpers/crossChainEnv.helper';
import { useEVMWallet } from '@/store/modules/wallet/useEVMWallet';
import { useSwap } from '@/store/modules/swap/useSwap';
import { useTokens } from '@/store/modules/tokens/useTokens';
import { DEFAULT_NETWORK_VALUE } from '@/constants/DEFAULT_NETWORK_VALUE';


export default /*#__PURE__*/_defineComponent({
  __name: 'SwapInputPanel',
  props: {
    field: null
  },
  emits: ["balanceError", "addToken"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'balanceError', value: boolean): void;
  (e: 'addToken'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  field: Fields;
}

const EMPTY_INPUT_VALUE = '';



const { walletState } = useEVMWallet();
const { swapForm, setToken, setAmount } = useSwap();
const { t } = useI18n();
const {
  getGasToken,
  getFirstPresentLocallyToken,
  getBLUESToken,
  getTokensListByChainId,
  getTokenBySymbolAndChainId,
} = useTokens();
const router = useRouter();
const route = useRoute();

const isPanelFocus = ref(false);
const setPanelFocus = (value: boolean) => {
  isPanelFocus.value = value;
};

const isBalanceError = ref(false);
const setBalanceError = event => {
  emits('balanceError', event);
  return (isBalanceError.value = event);
};

const caption = computed(() => t(`swap.inputCaption.${props.field}`));
const hideZeroBalances = ref(false);

const isInputDisabled = computed(() => !swapForm[props.field].token);
const inputValue = ref(swapForm[props.field].amount ?? EMPTY_INPUT_VALUE);
const isInputValueLoading = computed(
  () =>
    swapForm.bestTradeRequestInProgress &&
    !isInputDisabled.value &&
    swapForm[props.field].estimated,
);
const usdAmount = ref(null);
const maxFractionDigits = computed(() => {
  const milkomedaToken = swapForm[props.field].token;

  if (!milkomedaToken) {
    return 2;
  }
  return minDecimalsFromCardanoAndMilkomeda(milkomedaToken);
});

const isAddTokenButton = computed(() => {
  const commonDemand =
    swapForm[props.field].token &&
    !swapForm[props.field].token?.isETHToken() &&
    walletState.connector?.id === 'injected';
  return ENABLE_CROSS_CHAIN
    ? commonDemand && Number(swapForm[props.field].token?.chainId) === walletState.network?.chainId
    : commonDemand;
});
const addToWallet = token => {
  if (token) {
    addToken({
      address: token.address,
      symbol: token.symbol,
      decimals: token.decimals,
      image: token.tokenIconUrl,
    });
  }
};

const BlUESTokenInDefaultChain = computed(() => {
  // NOTE
  // Into FAKE_CARDANO network we have all tokens from milkomeda C1.
  const chainId = +(getNetworkId(DEFAULT_NETWORK_VALUE) ?? -1);

  return getTokensListByChainId(chainId).find(token => token.symbol === 'BLUES')!;
});
const BLUESToken = computed(() => {
  const localBLUESToken = getBLUESToken().description?.isPresentLocally ? getBLUESToken() : null;
  if (ENABLE_CROSS_CHAIN) {
    return localBLUESToken ? localBLUESToken : BlUESTokenInDefaultChain.value;
  }
  console.log(getBLUESToken().description?.isPresentLocally, localBLUESToken);
  return localBLUESToken;
});
const defaultToken = computed(() => {
  return props.field === Fields.InputA ? getGasToken() : BLUESToken.value;
});

const onChangeToken = token => {
  setToken(token, props.field as unknown as SWAP_FORM_INPUTS);
  if (hasTokensIntoUrl()) {
    router.replace(
      buildSwapWithTokenSymbolsRoute(
        swapForm[Fields.InputA].token?.symbol,
        swapForm[Fields.InputB].token?.symbol,
      ),
    );
  }
};

const onChangeInput = value => {
  setAmount(value, props.field as unknown as SWAP_FORM_INPUTS);
};

watch(
  swapForm[props.field],
  value => {
    inputValue.value = value?.amount ?? EMPTY_INPUT_VALUE;
  },
  { immediate: true, deep: true },
);

const getTokenFromUrl = (tokenSymbol: string, chainId: string): Token => {
  try {
    return getTokenBySymbolAndChainId(tokenSymbol, +chainId);
  } catch (e) {
    console.log(e);
    return getGasToken();
  }
};

function hasTokensIntoUrl() {
  return !!(route.params && route.params.fromTokenSymbol && route.params.toTokenSymbol);
}

watch(
  () => route.params,
  () => {
    // NOTE:
    // Do not reset tokens to default tokens after WSC swap dialog was closed.
    if (
      ENABLE_FAKE_CARDANO_NETWORK &&
      swapForm.input.token &&
      swapForm.output.token &&
      !hasTokensIntoUrl()
    ) {
      return;
    }

    if (route.params) {
      const isBluesOnlyFromUrl =
        route.params.fromTokenSymbol &&
        route.params.fromTokenSymbol.toString() === getBLUESToken().symbol &&
        !route.params.toTokenSymbol;

      swapForm[Fields.InputB].token =
        route.params.toTokenSymbol && DEFAULT_NETWORK_ID && !isBluesOnlyFromUrl
          ? getTokenFromUrl(route.params.toTokenSymbol.toString(), DEFAULT_NETWORK_ID)
          : BLUESToken.value;

      swapForm[Fields.InputA].token =
        route.params.fromTokenSymbol && DEFAULT_NETWORK_ID && !isBluesOnlyFromUrl
          ? getTokenFromUrl(route.params.fromTokenSymbol.toString(), DEFAULT_NETWORK_ID)
          : getGasToken() ?? getFirstPresentLocallyToken(swapForm[Fields.InputB].token);
    }
  },
  {
    immediate: true,
  },
);

return (_ctx: any,_cache: any) => {
  const _component_Skeleton = _resolveComponent("Skeleton")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["flex-column input-panel input-panel-xl line-height-1", { 'panel-focus': isPanelFocus.value, 'panel-error': isBalanceError.value }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("span", null, _toDisplayString(_unref(caption)), 1),
        (_unref(swapForm).estimatingInProgress)
          ? (_openBlock(), _createBlock(_component_Skeleton, { key: 0 }))
          : (_unref(swapForm)[__props.field].estimated)
            ? (_openBlock(), _createElementBlock("span", _hoisted_3, "(estimated)"))
            : _createCommentVNode("", true)
      ]),
      _createVNode(SwapInputBalance, {
        token: _unref(swapForm)[__props.field].token!,
        field: __props.field,
        onBalanceError: _cache[0] || (_cache[0] = ($event: any) => (setBalanceError($event)))
      }, null, 8, ["token", "field"])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createVNode(TokenSelect, {
          "hide-zero-balances": hideZeroBalances.value,
          fieldType: __props.field,
          onOnChangeSelect: _cache[1] || (_cache[1] = ($event: any) => (onChangeToken($event))),
          token: _unref(swapForm)[__props.field].token ?? _unref(defaultToken),
          showFilterZeroBalances: __props.field === _unref(Fields).InputA,
          "chain-select-disable": true
        }, null, 8, ["hide-zero-balances", "fieldType", "token", "showFilterZeroBalances"]),
        (_unref(isAddTokenButton))
          ? (_openBlock(), _createBlock(AddTokenButton, {
              key: 0,
              onClick: _cache[2] || (_cache[2] = ($event: any) => (addToWallet(_unref(swapForm)[__props.field].token))),
              class: "ml-s2"
            }))
          : _createCommentVNode("", true)
      ]),
      (_unref(isInputValueLoading))
        ? (_openBlock(), _createBlock(_component_Skeleton, {
            key: 0,
            height: "1.5rem",
            width: "5.5rem"
          }))
        : (_openBlock(), _createBlock(BaseInputNumber, {
            key: 1,
            modelValue: inputValue.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((inputValue).value = $event)),
            maxFractionDigits: _unref(maxFractionDigits),
            disabled: _unref(isInputDisabled),
            onInput: _cache[4] || (_cache[4] = ($event: any) => (onChangeInput($event))),
            onFocus: _cache[5] || (_cache[5] = ($event: any) => (setPanelFocus(true))),
            onBlur: _cache[6] || (_cache[6] = ($event: any) => (setPanelFocus(false))),
            "input-class": "input-xl ml-s3",
            min: 0
          }, null, 8, ["modelValue", "maxFractionDigits", "disabled"]))
    ]),
    (usdAmount.value)
      ? (_openBlock(), _createBlock(UsdInputValue, {
          key: 0,
          "usd-amount": usdAmount.value,
          "usd-loss": -0.1,
          "is-loading": _unref(swapForm).bestTradeRequestInProgress
        }, null, 8, ["usd-amount", "usd-loss", "is-loading"]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})